import { supabase } from '../lib/supabaseClient';

export interface CheckoutSessionResponse {
    sessionId: string;
    url: string;
}

export interface PortalSessionResponse {
    url: string;
}

const supabaseAnonKey =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) ||
    (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY) ||
    '';

/**
 * Create a Stripe checkout session and redirect user to checkout
 */
export async function createCheckoutSession(
    priceId: string,
    returnUrl?: string
): Promise<CheckoutSessionResponse> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error('No active session');
    }

    if (!supabaseAnonKey) {
        throw new Error('Supabase anon key not configured');
    }

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': supabaseAnonKey,
            },
            body: JSON.stringify({ priceId, returnUrl }),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data;
}

/**
 * Create a Stripe customer portal session for subscription management
 */
export async function createPortalSession(returnUrl?: string): Promise<PortalSessionResponse> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error('No active session');
    }

    if (!supabaseAnonKey) {
        throw new Error('Supabase anon key not configured');
    }

    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': supabaseAnonKey,
            },
            body: JSON.stringify({ returnUrl }),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create portal session');
    }

    const data = await response.json();
    return data;
}

/**
 * Redirect to Stripe checkout for the specified price
 */
export async function redirectToCheckout(priceId: string, returnUrl?: string): Promise<void> {
    const { url } = await createCheckoutSession(priceId, returnUrl);
    window.location.href = url;
}

/**
 * Redirect to Stripe customer portal
 */
export async function redirectToPortal(returnUrl?: string): Promise<void> {
    const { url } = await createPortalSession(returnUrl);
    window.location.href = url;
}

